/**
 * ANÁLISE BOT × DIA — derivação pura da aba Análise recriada (19/10).
 *
 * > "as etapas de conclusão, por dia, e uma análise dos bots para eu entender os
 * > fluxos por dia de cada um, os gaps, os que já concluíram mais empresas em
 * > menos tempo … e ver isso como o fluxo que organizei" — e depois: "com e sem
 * > movimento", "melhores bots por analista que tem mais volume".
 *
 * Tudo sai de UMA resposta (`/bots-por-dia`) + a ordem do Processos + o
 * movimento + os responsáveis + o UNIVERSO de cada fluxo (`/cobertura`) + os
 * FILTROS do Processos (regime, empresa, status — o mesmo `empresasFiltradas`).
 *
 * TUDO no mesmo recorte (dono, 19/10: "todos os dados... conclusão do fluxo dia a
 * dia, bot x dia etc"): contagens, tempo médio, ritmo e tentativas saem das
 * listas e das `metricas` por empresa, filtradas pela mesma função (`filtroDe`).
 *
 * MESMA RÉGUA DO PROCESSOS (dono, 19/10: "não podem ser diferentes"). O status
 * da empresa no bot é o ÚLTIMO desfecho do período, com o `skip` "feito"
 * contando como concluída — o backend já devolve as listas assim. E as empresas
 * contadas são as do universo do fluxo (vigentes do regime da área), como no
 * cartão: quem rodou fora do regime não entra no numerador nem no total.
 *
 * Só as JANELAS DE EXECUÇÃO (início, fim, quantas execuções) ficam globais: são
 * do bot, não de empresa — o backend declara isso em `execucoes_escopo`. Nenhum número é inventado por subtração: quando
 * o recorte de movimento está ligado, as contagens de empresa são refeitas
 * pelas LISTAS do dia; tentativas e tempo não têm lista por empresa e ficam como
 * vieram (a tela diz isso).
 */

import type { IBotDia, IBotPeriodo, IBotsPorDiaResposta, IEmpresaResumo } from '../api';
import type { OrdemDoFluxo } from './ordemDoFluxo';

export type RecorteMovimento = 'todos' | 'com' | 'sem';

/** Nome do grupo dos bots que não estão em fluxo nenhum da esteira. */
export const FLUXO_OUTROS = 'Fora dos fluxos';

const pertence = (codi: string, recorte: RecorteMovimento, com: Set<string> | null) => {
  if (recorte === 'todos' || !com) return true;
  return recorte === 'com' ? com.has(codi) : !com.has(codi);
};

/** Universo (vigentes do regime da área) de cada fluxo, pelo nome do fluxo. */
export type UniversosDosFluxos = Map<string, Set<string>>;

/**
 * Quem conta: dentro do universo do fluxo (quando conhecido), do recorte dos
 * filtros do Processos (`noRecorte`, null = sem filtro) e do recorte de movimento.
 */
const filtroDe =
  (
    universo: Set<string> | undefined,
    recorte: RecorteMovimento,
    com: Set<string> | null,
    noRecorte?: Set<string> | null,
  ) =>
  (codi: string) =>
    (!universo || universo.has(codi)) &&
    (!noRecorte || noRecorte.has(codi)) &&
    pertence(codi, recorte, com);

/** Soma de `[tentativas, tempo_total, tempo_ok, n_ok]` das empresas aceitas. */
export interface SomaMetricas {
  tentativas: number;
  tempoTotal: number;
  tempoOk: number;
  nOk: number;
}

const somarMetricas = (
  d: IBotDia,
  aceita: (codi: string) => boolean,
  acc: SomaMetricas = { tentativas: 0, tempoTotal: 0, tempoOk: 0, nOk: 0 },
): SomaMetricas => {
  (d.codis ?? []).forEach((codi, i) => {
    const m = d.metricas?.[i];
    if (!m || !aceita(codi)) return;
    acc.tentativas += m[0];
    acc.tempoTotal += m[1];
    acc.tempoOk += m[2];
    acc.nOk += m[3];
  });
  return acc;
};

const mediaOk = (s: SomaMetricas) => (s.nOk ? Math.round(s.tempoOk / s.nOk) : null);
const porHora = (concluidas: number, tempoMs: number) =>
  concluidas && tempoMs > 0 ? Math.round((concluidas / (tempoMs / 3_600_000)) * 100) / 100 : null;

/**
 * O dia do bot no recorte (universo do fluxo, filtros do Processos, movimento):
 * contagens refeitas pelas listas e, com `metricas`, tentativas e tempo também.
 */
export const diaNoRecorte = (
  d: IBotDia,
  recorte: RecorteMovimento,
  com: Set<string> | null,
  universo?: Set<string>,
  noRecorte?: Set<string> | null,
): IBotDia => {
  if ((recorte === 'todos' || !com) && !universo && !noRecorte) return d;
  const aceita = filtroDe(universo, recorte, com, noRecorte);
  const f = (l: string[]) => l.filter(aceita);
  const todos = d.codis ?? [];
  const codis = f(todos);
  const ok = f(d.ok_codis);
  const falha = f(d.falha_codis);
  const metricas = d.metricas
    ? todos.flatMap((c, i) => (aceita(c) && d.metricas?.[i] ? [d.metricas[i]] : []))
    : undefined;
  const soma = d.metricas ? somarMetricas(d, aceita) : null;
  return {
    ...d,
    codis,
    metricas,
    ok_codis: ok,
    falha_codis: falha,
    pendente_codis: d.pendente_codis ? f(d.pendente_codis) : d.pendente_codis,
    empresas: codis.length,
    concluidas: ok.length,
    so_falha: falha.length,
    pendentes: d.pendente_codis ? f(d.pendente_codis).length : d.pendentes,
    ...(soma
      ? {
          tentativas: soma.tentativas,
          tempo_total_ms: soma.tempoTotal,
          tempo_medio_ok_ms: mediaOk(soma),
          empresas_por_hora: porHora(ok.length, soma.tempoTotal),
        }
      : {}),
  };
};

export interface LinhaBot {
  bot: string;
  fluxo: string;
  grupo: string | null;
  totais: IBotPeriodo['totais'];
  /** Dia → dado (já no recorte de movimento). */
  porDia: Map<string, IBotDia>;
  /** Concluídas no período (último desfecho, régua do Processos) sob o recorte. */
  concluidas: number;
  /** Terminaram o período em falha (erro ou alerta, no Processos). */
  falhas: number;
  /**
   * O TOTAL do cartão: o universo do fluxo sob o recorte. Sem universo
   * conhecido, as empresas tocadas pelo bot (e `deUniverso` é false).
   */
  empresas: number;
  deUniverso: boolean;
  /** Concluídas / total (0–100); null sem empresa. */
  taxa: number | null;
  comMovimento: number;
  semMovimento: number;
  /** As listas de onde `concluidas` saiu (por analista, sem outra régua). */
  listas: { tocadas: Set<string>; concluidas: Set<string> };
  /**
   * Só na linha CONSOLIDADA de bot em várias áreas (`linhasPorBot`): as áreas
   * que ela soma, para a tela dizer de onde vem o total.
   */
  areas?: string[];
}

/**
 * Tocadas e concluídas do período, já no universo e no recorte. Guardadas na
 * linha por `blocosPorFluxo` (não recalculadas aqui, para não abrir outra régua).
 */
export const listasDoPeriodo = (l: LinhaBot): { tocadas: Set<string>; concluidas: Set<string> } =>
  l.listas;

export interface BlocoFluxo {
  fluxo: string;
  bots: LinhaBot[];
}

const grupoDoBot = (ordem: OrdemDoFluxo, fluxo: string, bot: string): string | null =>
  (ordem.gruposDoFluxo.get(fluxo) ?? []).find(g => g.bots.includes(bot))?.nome ?? null;

/** Fluxos do bot na ordem da tela; fora de todos → só `FLUXO_OUTROS`. */
const fluxosDe = (ordem: OrdemDoFluxo, bot: string): string[] => {
  const lista = ordem.fluxosDoBot?.get(bot);
  if (lista?.length) return lista;
  const primeiro = ordem.fluxoDoBot.get(bot);
  return primeiro !== undefined ? [primeiro] : [FLUXO_OUTROS];
};

/**
 * O universo de VÁRIOS fluxos juntos: a união dos universos. Se algum deles não
 * tem universo conhecido, não há como recortar sem esconder empresa — undefined
 * (conta as tocadas, como o fluxo sem universo já faz).
 */
const uniaoDosUniversos = (
  fluxos: string[],
  universos?: UniversosDosFluxos,
): Set<string> | undefined => {
  if (!universos) return undefined;
  const uniao = new Set<string>();
  for (let i = 0; i < fluxos.length; i += 1) {
    const u = universos.get(fluxos[i]);
    if (!u) return undefined;
    u.forEach(c => uniao.add(c));
  }
  return uniao;
};

/** A linha de UM bot num recorte de universo (o de um fluxo ou a união de vários). */
const linhaDoBot = (
  b: IBotPeriodo,
  fluxo: string,
  grupo: string | null,
  universo: Set<string> | undefined,
  recorte: RecorteMovimento,
  com: Set<string> | null,
  noRecorte?: Set<string> | null,
): LinhaBot => {
  const aceita = filtroDe(universo, recorte, com, noRecorte);
  const porDia = new Map(
    b.dias.map(d => [d.dia, diaNoRecorte(d, recorte, com, universo, noRecorte)]),
  );
  // Status do PERÍODO pelas listas do backend (último desfecho). Monitoramento
  // antigo, sem as listas: cai na união dos dias, que era a régua de antes.
  const conc = new Set<string>();
  const falhas = new Set<string>();
  const tocadas = new Set<string>();
  if (b.totais.ok_codis) {
    b.totais.ok_codis.filter(aceita).forEach(c => conc.add(c));
    (b.totais.falha_codis ?? []).filter(aceita).forEach(c => falhas.add(c));
    (b.totais.codis ?? []).filter(aceita).forEach(c => tocadas.add(c));
  } else {
    porDia.forEach(d => {
      d.ok_codis.forEach(c => conc.add(c));
      d.falha_codis.forEach(c => falhas.add(c));
      (d.codis ?? d.ok_codis).forEach(c => tocadas.add(c));
    });
    conc.forEach(c => falhas.delete(c));
  }
  // O total do cartão é o universo do fluxo (no recorte de movimento).
  const total = universo ? Array.from(universo).filter(aceita).length : tocadas.size;
  // Tempo, ritmo e tentativas no MESMO recorte, pelas métricas por empresa
  // (somadas dia a dia). Monitoramento antigo, sem métricas: ficam como vieram.
  const comMetricas = b.dias.some(d => d.metricas);
  let { totais } = b;
  if (comMetricas) {
    const soma: SomaMetricas = { tentativas: 0, tempoTotal: 0, tempoOk: 0, nOk: 0 };
    b.dias.forEach(d => somarMetricas(d, aceita, soma));
    totais = {
      ...b.totais,
      empresas: tocadas.size,
      concluidas: conc.size,
      tentativas: soma.tentativas,
      tempo_total_ms: soma.tempoTotal,
      tempo_medio_ok_ms: mediaOk(soma),
      empresas_por_hora: porHora(conc.size, soma.tempoTotal),
      tentativas_por_empresa: tocadas.size
        ? Math.round((soma.tentativas / tocadas.size) * 100) / 100
        : null,
      dias_com_conclusao: Array.from(porDia.values()).filter(d => d.concluidas > 0).length,
    };
  }
  let comM = 0;
  let semM = 0;
  if (com) {
    conc.forEach(c => {
      if (com.has(c)) comM += 1;
      else semM += 1;
    });
  }
  return {
    bot: b.bot,
    fluxo,
    grupo,
    totais,
    porDia,
    concluidas: conc.size,
    falhas: falhas.size,
    empresas: total,
    deUniverso: Boolean(universo),
    taxa: total ? (conc.size / total) * 100 : null,
    comMovimento: comM,
    semMovimento: semM,
    listas: { tocadas, concluidas: conc },
  };
};

/**
 * Bots agrupados por fluxo, na ORDEM DO PROCESSOS (fluxo → grupo → bot).
 *
 * Bot em N fluxos aparece em CADA um, contado no universo daquele fluxo — como
 * o Processos faz por área. Antes ia só para o primeiro: o BOT_EFD_REINF (Fiscal
 * SN, LP e LR) mostrava 292 de 534 concluídas, só a fatia do Lucro Presumido.
 */
export const blocosPorFluxo = (
  resp: IBotsPorDiaResposta | undefined,
  ordem: OrdemDoFluxo,
  recorte: RecorteMovimento,
  com: Set<string> | null,
  universos?: UniversosDosFluxos,
  noRecorte?: Set<string> | null,
): BlocoFluxo[] => {
  const linhas: LinhaBot[] = (resp?.bots ?? []).flatMap(b =>
    fluxosDe(ordem, b.bot).map(fluxo =>
      linhaDoBot(
        b,
        fluxo,
        grupoDoBot(ordem, fluxo, b.bot),
        universos?.get(fluxo),
        recorte,
        com,
        noRecorte,
      ),
    ),
  );
  const posicao = (bot: string) => ordem.posicaoDoBot.get(bot) ?? Number.MAX_SAFE_INTEGER;
  const nomes = [...ordem.fluxos, FLUXO_OUTROS];
  return nomes
    .map(fluxo => ({
      fluxo: fluxo || 'Bots sem grupo',
      bots: linhas
        .filter(l => l.fluxo === fluxo)
        .sort((a, b) => posicao(a.bot) - posicao(b.bot) || b.concluidas - a.concluidas),
    }))
    .filter(b => b.bots.length > 0);
};

/**
 * UMA linha por bot sobre os blocos visíveis — para ranking, destaques e gaps,
 * onde o bot repetido em cada área apareceria N vezes. Bot num bloco só fica
 * como está; em vários, é refeito sobre a UNIÃO dos universos desses fluxos
 * (REINF: 189 + 292 + 33 = 514 concluídas no regime, não a fatia de uma área).
 * O `fluxo` da linha vira os nomes juntos ("Fiscal SN · Fiscal LP").
 */
export const linhasPorBot = (
  blocos: BlocoFluxo[],
  resp: IBotsPorDiaResposta | undefined,
  recorte: RecorteMovimento,
  com: Set<string> | null,
  universos?: UniversosDosFluxos,
  noRecorte?: Set<string> | null,
): LinhaBot[] => {
  const porBot = new Map<string, LinhaBot[]>();
  blocos.forEach(bl => bl.bots.forEach(l => porBot.set(l.bot, [...(porBot.get(l.bot) ?? []), l])));
  const dadosDe = new Map((resp?.bots ?? []).map(b => [b.bot, b]));
  return Array.from(porBot.values()).map(ls => {
    const b = dadosDe.get(ls[0].bot);
    if (ls.length === 1 || !b) return ls[0];
    const fluxos = ls.map(l => l.fluxo);
    const areas = fluxos.map(f => f || 'Bots sem grupo');
    return {
      ...linhaDoBot(
        b,
        areas.join(' · '),
        ls[0].grupo,
        uniaoDosUniversos(fluxos, universos),
        recorte,
        com,
        noRecorte,
      ),
      areas,
    };
  });
};

/**
 * Blocos do mapa Bot × dia SEM repetir bot (24/10, dono: "quero ver o valor
 * real"). Bot em várias áreas fica UMA vez, no bloco da primeira área visível,
 * com a linha consolidada de `linhasPorBot` (REINF: 514, não 189/292/33); as
 * `areas` dela dizem de onde vem o total.
 */
export const blocosSemRepeticao = (blocos: BlocoFluxo[], linhas: LinhaBot[]): BlocoFluxo[] => {
  const consolidada = new Map(linhas.map(l => [l.bot, l]));
  const vistos = new Set<string>();
  return blocos
    .map(bl => ({
      fluxo: bl.fluxo,
      bots: bl.bots.flatMap(l => {
        if (vistos.has(l.bot)) return [];
        vistos.add(l.bot);
        return [consolidada.get(l.bot) ?? l];
      }),
    }))
    .filter(bl => bl.bots.length > 0);
};

// ── Fluxo por dia ───────────────────────────────────────────────────────────

export interface DiaDoFluxo {
  dia: string;
  /**
   * Empresas que fecharam a ÚLTIMA etapa no dia, pela régua da etapa do
   * Processos: concluíram em algum RPA dela e NÃO terminaram o dia em erro em
   * nenhum outro (erro em um RPA derruba a etapa inteira).
   */
  fecharam: number;
  /** Empresas distintas tocadas por qualquer bot do fluxo no dia. */
  emAndamento: number;
  /**
   * Situação da última etapa AO FIM deste dia, pelo último desfecho de cada
   * empresa em cada RPA até aqui — no último dia é o número do cartão da etapa.
   * Pode cair: a empresa que concluiu e depois falhou sai.
   */
  acumulado: number;
}

type Desfecho = 'ok' | 'err' | 'pend';

/** Etapa concluída: ok em algum RPA e erro em nenhum (o `statusEfetivo` do Processos). */
const etapaConcluida = (porBot: Array<Desfecho | undefined>): boolean =>
  porBot.includes('ok') && !porBot.includes('err');

const desfechoDoDia = (d: IBotDia): Map<string, Desfecho> => {
  const m = new Map<string, Desfecho>();
  (d.pendente_codis ?? []).forEach(c => m.set(c, 'pend'));
  d.falha_codis.forEach(c => m.set(c, 'err'));
  d.ok_codis.forEach(c => m.set(c, 'ok'));
  return m;
};

export interface FluxoPorDia {
  fluxo: string;
  ultimaEtapa: string | null;
  dias: DiaDoFluxo[];
  totalFecharam: number;
}

export const fluxosPorDia = (
  resp: IBotsPorDiaResposta | undefined,
  ordem: OrdemDoFluxo,
  recorte: RecorteMovimento,
  com: Set<string> | null,
  universos?: UniversosDosFluxos,
  noRecorte?: Set<string> | null,
): FluxoPorDia[] => {
  if (!resp) return [];
  const porBot = new Map(resp.bots.map(b => [b.bot, b]));
  return ordem.fluxos
    .map(fluxo => {
      const grupos = (ordem.gruposDoFluxo.get(fluxo) ?? []).filter(g => g.bots.length);
      const ultimo = grupos[grupos.length - 1];
      const todos = grupos.flatMap(g => g.bots);
      const botsDaUltima = ultimo?.bots ?? [];
      // Último desfecho de cada empresa em cada RPA da última etapa, até o dia.
      const estado = new Map<string, Map<string, Desfecho>>(botsDaUltima.map(b => [b, new Map()]));
      const dias = resp.dias.map(dia => {
        const tocadas = new Set<string>();
        const doDia = new Map<string, Map<string, Desfecho>>();
        todos.forEach(bot => {
          const d0 = porBot.get(bot)?.dias.find(x => x.dia === dia);
          if (!d0) return;
          const d = diaNoRecorte(d0, recorte, com, universos?.get(fluxo), noRecorte);
          (d.codis ?? d.ok_codis).forEach(c => tocadas.add(c));
          if (!botsDaUltima.includes(bot)) return;
          const desf = desfechoDoDia(d);
          doDia.set(bot, desf);
          desf.forEach((v, c) => estado.get(bot)?.set(c, v));
        });
        const etapa = (fonte: Map<string, Map<string, Desfecho>>) => {
          const empresas = new Set<string>();
          fonte.forEach(m => m.forEach((_v, c) => empresas.add(c)));
          return Array.from(empresas).filter(c =>
            etapaConcluida(botsDaUltima.map(b => fonte.get(b)?.get(c))),
          ).length;
        };
        return {
          dia,
          fecharam: etapa(doDia),
          emAndamento: tocadas.size,
          acumulado: etapa(estado),
        };
      });
      return {
        fluxo: fluxo || 'Bots sem grupo',
        ultimaEtapa: ultimo?.nome ?? null,
        dias,
        totalFecharam: dias.length ? dias[dias.length - 1].acumulado : 0,
      };
    })
    .filter(f => f.dias.some(d => d.emAndamento > 0));
};

/** Nome da linha que soma os fluxos na "Conclusão do fluxo, dia a dia". */
export const TOTAL_DOS_FLUXOS = 'Todos os fluxos';

/**
 * A SOMA dos fluxos visíveis, dia a dia (24/10). Cada fluxo continua com a sua
 * régua (a última etapa dele, no universo da área); esta linha só soma, para
 * dar o total do período sem o dono somar de cabeça. É soma de conclusões de
 * fluxo, não de empresas distintas: a mesma empresa que fecha o Fiscal e o
 * Contábil conta nos dois. Com um fluxo só, não há o que somar (null).
 */
export const totalDosFluxos = (fluxos: FluxoPorDia[]): FluxoPorDia | null => {
  if (fluxos.length < 2) return null;
  const soma = (f: (d: DiaDoFluxo) => number, i: number) =>
    fluxos.reduce((acc, fl) => acc + (fl.dias[i] ? f(fl.dias[i]) : 0), 0);
  return {
    fluxo: TOTAL_DOS_FLUXOS,
    ultimaEtapa: null,
    dias: fluxos[0].dias.map((d, i) => ({
      dia: d.dia,
      fecharam: soma(x => x.fecharam, i),
      emAndamento: soma(x => x.emAndamento, i),
      acumulado: soma(x => x.acumulado, i),
    })),
    totalFecharam: fluxos.reduce((acc, f) => acc + f.totalFecharam, 0),
  };
};

// ── Gaps ────────────────────────────────────────────────────────────────────

export interface GapDoBot {
  bot: string;
  fluxo: string;
  /** Dias, entre o primeiro e o último dia com atividade, em que o bot não rodou. */
  diasParado: string[];
  /** Maior sequência de dias seguidos sem rodar (dentro da janela ativa). */
  maiorSequencia: { dias: number; de: string; ate: string } | null;
  /** Dias em que rodou e não concluiu empresa nenhuma. */
  rodouSemConcluir: string[];
  /** Dias desde a última atividade até o fim da janela. */
  paradoDesde: number;
}

export const gapsDosBots = (blocos: BlocoFluxo[], dias: string[]): GapDoBot[] => {
  const out: GapDoBot[] = [];
  blocos.forEach(bl =>
    bl.bots.forEach(l => {
      const ativos = dias.filter(d => {
        const x = l.porDia.get(d);
        return x && (x.execucoes > 0 || x.tentativas > 0);
      });
      if (!ativos.length) return;
      const i0 = dias.indexOf(ativos[0]);
      const i1 = dias.indexOf(ativos[ativos.length - 1]);
      const diasParado: string[] = [];
      let seq: GapDoBot['maiorSequencia'] = null;
      let atual: string[] = [];
      for (let i = i0; i <= i1; i += 1) {
        const x = l.porDia.get(dias[i]);
        const rodou = x && (x.execucoes > 0 || x.tentativas > 0);
        if (!rodou) {
          diasParado.push(dias[i]);
          atual.push(dias[i]);
        }
        if (rodou || i === i1) {
          if (atual.length && (!seq || atual.length > seq.dias)) {
            seq = { dias: atual.length, de: atual[0], ate: atual[atual.length - 1] };
          }
          atual = [];
        }
      }
      const rodouSemConcluir = ativos.filter(d => {
        const x = l.porDia.get(d);
        return x && x.tentativas > 0 && x.concluidas === 0;
      });
      out.push({
        bot: l.bot,
        fluxo: bl.fluxo,
        diasParado,
        maiorSequencia: seq,
        rodouSemConcluir,
        paradoDesde: dias.length - 1 - i1,
      });
    }),
  );
  return out
    .filter(g => g.diasParado.length || g.rodouSemConcluir.length || g.paradoDesde > 0)
    .sort(
      (a, b) =>
        b.rodouSemConcluir.length +
        b.diasParado.length -
        (a.rodouSemConcluir.length + a.diasParado.length),
    );
};

// ── Ranking ─────────────────────────────────────────────────────────────────

export type CriterioRanking = 'concluidas' | 'ritmo' | 'tempo' | 'taxa' | 'retentativa';

/** Amostra mínima para entrar nos destaques de ritmo/tempo (poucas = ruído). */
export const AMOSTRA_MINIMA = 10;

export const ordenarRanking = (linhas: LinhaBot[], criterio: CriterioRanking): LinhaBot[] => {
  const v = (l: LinhaBot): number => {
    switch (criterio) {
      case 'ritmo':
        return l.totais.empresas_por_hora ?? -1;
      case 'tempo':
        // Menor tempo primeiro: inverte o sinal; sem medida vai para o fim.
        return l.totais.tempo_medio_ok_ms === null ? -Infinity : -l.totais.tempo_medio_ok_ms;
      case 'taxa':
        return l.taxa ?? -1;
      case 'retentativa':
        return l.totais.tentativas_por_empresa ?? -1;
      default:
        return l.concluidas;
    }
  };
  return [...linhas].sort((a, b) => v(b) - v(a) || b.concluidas - a.concluidas);
};

export interface Destaques {
  maisConcluiu: LinhaBot | null;
  maisRapido: LinhaBot | null;
  melhorRitmo: LinhaBot | null;
  maisRetentativa: LinhaBot | null;
}

export const destaques = (linhas: LinhaBot[]): Destaques => {
  const comAmostra = linhas.filter(l => l.concluidas >= AMOSTRA_MINIMA);
  const melhor = (lista: LinhaBot[], f: (l: LinhaBot) => number | null, menor = false) =>
    lista.reduce<LinhaBot | null>((acc, l) => {
      const x = f(l);
      if (x === null) return acc;
      if (!acc) return l;
      const y = f(acc) as number;
      return (menor ? x < y : x > y) ? l : acc;
    }, null);
  return {
    maisConcluiu: melhor(linhas, l => (l.concluidas ? l.concluidas : null)),
    maisRapido: melhor(comAmostra, l => l.totais.tempo_medio_ok_ms, true),
    melhorRitmo: melhor(comAmostra, l => l.totais.empresas_por_hora),
    maisRetentativa: melhor(
      linhas.filter(l => l.empresas >= AMOSTRA_MINIMA),
      l => l.totais.tentativas_por_empresa,
    ),
  };
};

// ── Analistas ───────────────────────────────────────────────────────────────

/** Área de responsável que corresponde ao fluxo (o fiscal responde pelo Fiscal SN…). */
export const areaDoFluxo = (fluxo: string): string | null => {
  if (/fiscal/i.test(fluxo)) return 'fiscal';
  if (/cont[aá]b/i.test(fluxo)) return 'contabil';
  if (/pessoal|\bdp\b|folha|depto/i.test(fluxo)) return 'pessoal';
  return null;
};

export interface LinhaAnalista {
  analista: string;
  area: string;
  /** Empresas da carteira dele tocadas pelos bots do fluxo da área. */
  empresas: number;
  concluidas: number;
  taxa: number | null;
  /** Os bots que mais concluíram empresas dele, em ordem. */
  topBots: Array<{ bot: string; concluidas: number; empresas: number }>;
}

/**
 * Por ANALISTA: o responsável da área da empresa (fiscal/contábil/pessoal),
 * cruzado com os bots do fluxo daquela área. Ordenado por volume.
 */
export const porAnalista = (
  blocos: BlocoFluxo[],
  empresas: IEmpresaResumo[],
  responsaveis: Record<string, Record<string, string>> | undefined,
): LinhaAnalista[] => {
  const porCodi = new Map(empresas.map(e => [e.codi_emp, e]));
  const respDe = (codi: string, area: string): string | null => {
    const e = porCodi.get(codi);
    const mapa = e?.areas && Object.keys(e.areas).length ? e.areas : responsaveis?.[codi];
    return mapa?.[area] ?? null;
  };
  const cacheResp = new Map<string, string | null>();
  const resp = (codi: string, area: string) => {
    const k = `${area}|${codi}`;
    if (!cacheResp.has(k)) cacheResp.set(k, respDe(codi, area));
    return cacheResp.get(k) ?? null;
  };

  type Acc = {
    area: string;
    tocadas: Set<string>;
    conc: Set<string>;
    bots: Map<string, { c: Set<string>; t: Set<string> }>;
  };
  const acc = new Map<string, Acc>();
  blocos.forEach(bl => {
    const area = areaDoFluxo(bl.fluxo);
    if (!area) return;
    bl.bots.forEach(l => {
      // As listas do período já no universo e no recorte (ver `blocosPorFluxo`).
      const { tocadas, concluidas } = listasDoPeriodo(l);
      tocadas.forEach(codi => {
        const quem = resp(codi, area);
        if (!quem) return;
        const k = `${area}|${quem}`;
        const a = acc.get(k) ?? { area, tocadas: new Set(), conc: new Set(), bots: new Map() };
        a.tocadas.add(codi);
        const b = a.bots.get(l.bot) ?? { c: new Set(), t: new Set() };
        b.t.add(codi);
        if (concluidas.has(codi)) {
          a.conc.add(codi);
          b.c.add(codi);
        }
        a.bots.set(l.bot, b);
        acc.set(k, a);
      });
    });
  });
  return Array.from(acc.entries())
    .map(([k, a]) => ({
      analista: k.split('|').slice(1).join('|'),
      area: a.area,
      empresas: a.tocadas.size,
      concluidas: a.conc.size,
      taxa: a.tocadas.size ? (a.conc.size / a.tocadas.size) * 100 : null,
      topBots: Array.from(a.bots.entries())
        .map(([bot, x]) => ({ bot, concluidas: x.c.size, empresas: x.t.size }))
        .sort((x, y) => y.concluidas - x.concluidas || y.empresas - x.empresas)
        .slice(0, 3),
    }))
    .sort((x, y) => y.empresas - x.empresas || y.concluidas - x.concluidas);
};

/** "3h 12min", "45 s" — duração legível para o tempo médio por empresa. */
export const duracaoCurta = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const min = s / 60;
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${String(Math.round(min % 60)).padStart(2, '0')}min`;
};

/** "seg 13/10" a partir de "2026-10-13". */
export const rotuloDoDia = (dia: string): { semana: string; data: string } => {
  const [a, m, d] = dia.split('-').map(Number);
  const dt = new Date(a, m - 1, d);
  const semana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][dt.getDay()];
  return { semana, data: `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}` };
};

/** Tom da célula pela taxa de sucesso do dia (concluídas ÷ empresas tocadas). */
export const tomDaCelula = (
  d: IBotDia | undefined,
): 'vazio' | 'idle' | 'err' | 'warn' | 'run' | 'ok' => {
  if (!d || (!d.execucoes && !d.tentativas)) return 'vazio';
  if (!d.empresas) return 'idle';
  const taxa = d.concluidas / d.empresas;
  if (taxa < 0.4) return 'err';
  if (taxa < 0.75) return 'warn';
  if (taxa < 1) return 'run';
  return 'ok';
};
